import { z } from 'zod'
import { Description } from '../../_kernel/Description'
import { Model } from '../../_kernel/architecture'

export const PropertyDescriptionValidation = z.object({
  amenities: z
    .instanceof(Description, { message: '[PropertyDescription] amenities is required' })
    .optional(),
  businessAmenities: z
    .instanceof(Description, { message: '[PropertyDescription] businessAmenities is required' })
    .optional(),
  attractions: z
    .instanceof(Description, { message: '[PropertyDescription] attractions is required' })
    .optional(),
  location: z
    .instanceof(Description, { message: '[PropertyDescription] location is required' })
    .optional(),
  headline: z
    .instanceof(Description, { message: '[PropertyDescription] headline is required' })
    .optional(),
  dining: z
    .instanceof(Description, { message: '[PropertyDescription] dining is required' })
    .optional(),
  rooms: z
    .instanceof(Description, { message: '[PropertyDescription] rooms is required' })
    .optional(),
})

export class PropertyDescription extends Model {
  static create({
    amenities,
    businessAmenities,
    attractions,
    location,
    headline,
    dining,
    rooms,
  }: z.infer<typeof PropertyDescriptionValidation>) {
    PropertyDescriptionValidation.parse({
      amenities,
      businessAmenities,
      attractions,
      location,
      headline,
      dining,
      rooms,
    })
    return new PropertyDescription(
      amenities,
      businessAmenities,
      attractions,
      location,
      headline,
      dining,
      rooms
    )
  }

  static fromJSON({ json }: { json: Partial<ReturnType<PropertyDescription['toJSON']>> }) {
    const params = {
      amenities: json.amenities ? Description.create({ value: json.amenities }) : undefined,
      businessAmenities: json.businessAmenities
        ? Description.create({ value: json.businessAmenities })
        : undefined,
      attractions: json.attractions ? Description.create({ value: json.attractions }) : undefined,
      location: json.location ? Description.create({ value: json.location }) : undefined,
      headline: json.headline ? Description.create({ value: json.headline }) : undefined,
      dining: json.dining ? Description.create({ value: json.dining }) : undefined,
      rooms: json.rooms ? Description.create({ value: json.rooms }) : undefined,
    }

    return PropertyDescription.create(params)
  }

  constructor(
    private readonly _amenities?: Description,
    private readonly _businessAmenities?: Description,
    private readonly _attractions?: Description,
    private readonly _location?: Description,
    private readonly _headline?: Description,
    private readonly _dining?: Description,
    private readonly _rooms?: Description
  ) {
    super()
  }

  get amenities() { return this._amenities?.value } // prettier-ignore
  get businessAmenities() { return this._businessAmenities?.value } // prettier-ignore
  get attractions() { return this._attractions?.value } // prettier-ignore
  get location() { return this._location?.value } // prettier-ignore
  get headline() { return this._headline?.value } // prettier-ignore
  get dining() { return this._dining?.value } // prettier-ignore
  get rooms() { return this._rooms?.value } // prettier-ignore

  description() {
    return (
      this.headline ||
      this.location ||
      this.rooms ||
      this.amenities ||
      this.businessAmenities ||
      this.dining ||
      this.attractions ||
      ''
    )
  }

  toJSON() {
    return {
      amenities: this._amenities?.value ?? null,
      businessAmenities: this._businessAmenities?.value ?? null,
      attractions: this._attractions?.value ?? null,
      location: this._location?.value ?? null,
      headline: this._headline?.value ?? null,
      dining: this._dining?.value ?? null,
      rooms: this._rooms?.value ?? null,
    }
  }
}
